import { AddAffordance, EmptyState } from '@atlas/ui-components'
import { Button, IconButton, Input } from '@atlas/ui-primitives'
import { Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { type ArticlePalette, DEFAULT_SWATCH_COLOR, normalizeHexInput } from '@/lib/article-palette'

/**
 * The brand-colours matrix: named palettes down the block, swatches across each
 * one, every swatch showing its colour and that colour's hex code.
 *
 * Read-only and editable are the same component so the two can never render a
 * palette differently — the Brand details tab shows exactly what the author built.
 */

export interface PaletteMatrixProps {
	palettes: ArticlePalette[]
	/** Omitted renders read-only: no pickers, no add/remove, just colours and codes. */
	onChange?: (palettes: ArticlePalette[]) => void
}

/** One colour: the swatch itself, and its hex code as text beneath. */
function ReadOnlySwatch({ color }: { color: string }) {
	return (
		<div className="w-20">
			<div
				className="h-14 w-full rounded-ds-sm border border-ds-border"
				// The one place a literal colour is correct: this is the article's own
				// data, not a design decision. Design tokens cannot express it.
				style={{ backgroundColor: color }}
				// A colour is not text, so a reader who cannot see it gets the code.
				role="img"
				aria-label={`Colour ${color}`}
			/>
			<p className="mt-1 font-mono text-ds-2xs uppercase text-ds-text-muted">{color}</p>
		</div>
	)
}

/**
 * One editable colour: a native colour picker over the swatch, and its hex code as
 * a text field. The two are one value in both directions.
 */
function EditableSwatch({
	color,
	paletteName,
	index,
	onChange,
	onRemove,
}: {
	color: string
	paletteName: string
	index: number
	onChange: (color: string) => void
	onRemove: () => void
}) {
	// The field holds what the author is typing, which may be a half-finished hex
	// value. The swatch's stored colour only moves when the draft becomes valid, so
	// typing `#f` never clears the colour or drops the swatch.
	const [draft, setDraft] = useState(color)
	const label = `${paletteName} colour ${index + 1}`

	const commit = (value: string) => {
		setDraft(value)
		const normalized = normalizeHexInput(value)
		if (normalized) onChange(normalized)
	}

	return (
		<div className="group/swatch relative w-20">
			<label className="block cursor-pointer">
				<span className="sr-only">{label}</span>
				<span className="block h-14 w-full rounded-ds-sm border border-ds-border" style={{ backgroundColor: color }} />
				<input
					type="color"
					value={color}
					aria-label={label}
					onChange={(event) => {
						setDraft(event.target.value)
						onChange(event.target.value)
					}}
					className="sr-only"
				/>
			</label>
			<Input
				value={draft}
				aria-label={`${label} hex code`}
				onChange={(event) => commit(event.target.value)}
				// Reverts the visible draft to the stored colour, so an abandoned
				// half-typed value does not sit there looking applied.
				onBlur={() => setDraft(color)}
				className="mt-1 h-7 px-1.5 text-center font-mono text-ds-2xs uppercase"
			/>
			<IconButton
				aria-label={`Remove ${label}`}
				variant="muted"
				size="sm"
				onPress={onRemove}
				className="absolute -right-2 -top-2 h-5 w-5 opacity-0 transition-opacity group-hover/swatch:opacity-100"
			>
				<X className="h-3 w-3" />
			</IconButton>
		</div>
	)
}

/** One palette: its name, then its swatches in the author's order. */
function PaletteRow({
	palette,
	onChange,
	onRemove,
}: {
	palette: ArticlePalette
	onChange?: (palette: ArticlePalette) => void
	onRemove?: () => void
}) {
	const setColor = (index: number, color: string) =>
		onChange?.({ ...palette, colors: palette.colors.map((existing, i) => (i === index ? color : existing)) })

	return (
		<section className="space-y-2">
			<div className="flex items-center gap-2">
				{onChange ? (
					<Input
						value={palette.name}
						aria-label="Palette name"
						placeholder="Palette name"
						onChange={(event) => onChange({ ...palette, name: event.target.value })}
						className="h-8 max-w-64 font-medium"
					/>
				) : (
					<p className="font-medium text-ds-text">{palette.name}</p>
				)}
				{onRemove ? (
					<IconButton aria-label={`Remove ${palette.name || 'palette'}`} variant="muted" size="sm" onPress={onRemove}>
						<Trash2 className="h-3.5 w-3.5" />
					</IconButton>
				) : null}
			</div>

			<div className="flex flex-wrap items-start gap-3">
				{palette.colors.map((color, index) =>
					onChange ? (
						// Keyed by POSITION, never by colour: keying on the value remounts the
						// input the moment the colour changes, which drops focus and the
						// half-typed hex draft on the first valid keystroke.
						<EditableSwatch
							key={index}
							color={color}
							paletteName={palette.name || 'Palette'}
							index={index}
							onChange={(next) => setColor(index, next)}
							onRemove={() => onChange({ ...palette, colors: palette.colors.filter((_, i) => i !== index) })}
						/>
					) : (
						<ReadOnlySwatch key={index} color={color} />
					),
				)}

				{onChange ? (
					<Button
						variant="dashed"
						size="sm"
						onPress={() => onChange({ ...palette, colors: [...palette.colors, DEFAULT_SWATCH_COLOR] })}
						className="h-14 w-20 flex-col gap-0.5 rounded-ds-sm px-0"
					>
						<Plus className="h-4 w-4" />
						<span className="text-ds-3xs">Colour</span>
					</Button>
				) : null}

				{/* An empty palette is valid, so it says so rather than looking broken. */}
				{palette.colors.length === 0 && !onChange ? <EmptyState title="No colours in this palette." /> : null}
			</div>
		</section>
	)
}

export function PaletteMatrix({ palettes, onChange }: PaletteMatrixProps) {
	const setPalette = (index: number, palette: ArticlePalette) =>
		onChange?.(palettes.map((existing, i) => (i === index ? palette : existing)))

	return (
		<div className="space-y-5">
			{palettes.map((palette, index) => (
				<PaletteRow
					key={index}
					palette={palette}
					onChange={onChange ? (next) => setPalette(index, next) : undefined}
					onRemove={onChange ? () => onChange(palettes.filter((_, i) => i !== index)) : undefined}
				/>
			))}

			{onChange ? (
				<AddAffordance
					label="Add palette"
					onPress={() => onChange([...palettes, { name: '', colors: [DEFAULT_SWATCH_COLOR] }])}
				/>
			) : null}
		</div>
	)
}
